/**
 * Settings → Security (SUPER_ADMIN scope).
 *
 * Three sub-sections, segmented control at the top:
 *   * Password Requirements — min length, complexity, expiry, reuse,
 *     lockout, session timeout
 *   * Active Sessions — every non-revoked refresh token in the org,
 *     with force-logout actions + emergency "logout everyone"
 *   * Login Activity — append-only auth event feed
 *
 * The existing ``SecuritySettings`` component handles per-user
 * self-service (change password, MFA, own sessions). This component
 * is the admin counterpart and renders alongside it on the same tab.
 */
import { useEffect, useMemo, useState } from "react";
import {
  Loader2,
  LogOut,
  Search,
  ShieldAlert,
  Unlock,
} from "lucide-react";
import { Card, CardHeader, CardTitle } from "@/components/ui/Card";
import { Button } from "@/components/ui/Button";
import { Input } from "@/components/ui/Input";
import { Select } from "@/components/ui/Select";
import { Modal } from "@/components/ui/Modal";
import { useToast } from "@/hooks/useToast";
import { formatDate } from "@/lib/utils";
import {
  adminForceLogoutOrg,
  adminForceLogoutSession,
  adminUnlockUser,
  getSecurityPolicy,
  listLoginEvents,
  listOrgSessions,
  updateSecurityPolicy,
  type LoginEventRow,
  type LoginEventType,
  type SecurityPolicy,
  type SessionRow,
} from "@/api/security";

type Section = "policy" | "sessions" | "events";

const EVENT_LABELS: Record<LoginEventType, string> = {
  login_success: "Login",
  login_failed: "Login failed",
  logout: "Logout",
  password_changed: "Password changed",
  password_reset_requested: "Reset requested",
  password_reset_completed: "Reset completed",
  account_locked: "Account locked",
  account_unlocked: "Account unlocked",
  session_force_logout: "Force logout",
};

const EVENT_TONE_CLASS: Record<LoginEventType, string> = {
  login_success: "bg-success/10 text-success",
  login_failed: "bg-danger/10 text-danger",
  logout: "bg-text-muted/10 text-text-muted",
  password_changed: "bg-accent/10 text-accent",
  password_reset_requested: "bg-warning/10 text-warning",
  password_reset_completed: "bg-success/10 text-success",
  account_locked: "bg-danger/10 text-danger",
  account_unlocked: "bg-success/10 text-success",
  session_force_logout: "bg-warning/10 text-warning",
};

export function OrgSecurityPolicySettings() {
  const [section, setSection] = useState<Section>("policy");

  return (
    <div className="space-y-4">
      <div className="flex items-center gap-1 flex-wrap">
        {(
          [
            { id: "policy", label: "Password Requirements" },
            { id: "sessions", label: "Active Sessions" },
            { id: "events", label: "Login Activity" },
          ] as { id: Section; label: string }[]
        ).map((s) => (
          <button
            key={s.id}
            onClick={() => setSection(s.id)}
            className={`px-3 py-1 text-xs rounded-full border transition-colors ${
              section === s.id
                ? "bg-accent text-white border-accent"
                : "border-border text-text-muted hover:bg-surface-hover"
            }`}
          >
            {s.label}
          </button>
        ))}
      </div>

      {section === "policy" && <PolicySection />}
      {section === "sessions" && <SessionsSection />}
      {section === "events" && <LoginEventsSection />}
    </div>
  );
}

// ── Policy ─────────────────────────────────────────────────────────

function PolicySection() {
  const { addToast } = useToast();
  const [policy, setPolicy] = useState<SecurityPolicy | null>(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    getSecurityPolicy()
      .then(setPolicy)
      .catch(() => addToast("error", "Couldn't load policy"))
      .finally(() => setLoading(false));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const save = async (patch: Partial<SecurityPolicy>) => {
    if (!policy) return;
    setSaving(true);
    try {
      const next = await updateSecurityPolicy(patch);
      setPolicy(next);
      addToast("success", "Policy updated");
    } catch (err: unknown) {
      const ax = err as { response?: { data?: { detail?: string } } };
      addToast("error", ax.response?.data?.detail || "Update failed");
    } finally {
      setSaving(false);
    }
  };

  if (loading || !policy) {
    return (
      <Card>
        <div className="py-10 text-center text-text-muted">
          <Loader2 className="h-6 w-6 animate-spin mx-auto" />
        </div>
      </Card>
    );
  }

  return (
    <Card>
      <CardHeader>
        <CardTitle>Password Requirements</CardTitle>
        {saving && <span className="text-xs text-text-muted">Saving…</span>}
      </CardHeader>

      <p className="text-xs text-text-muted mb-4">
        Applies to every password set in this organization &mdash;
        registration, invitations, resets, and self-service changes.
      </p>

      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
        <div>
          <label className="block text-xs font-medium text-text-muted mb-1">
            Minimum length
          </label>
          <Select
            value={String(policy.min_password_length)}
            onChange={(e) =>
              save({
                min_password_length: Number(e.target.value) as 8 | 10 | 12,
              })
            }
            options={[
              { value: "8", label: "8 characters" },
              { value: "10", label: "10 characters" },
              { value: "12", label: "12 characters" },
            ]}
          />
        </div>

        <ToggleRow
          label="Require uppercase letter"
          checked={policy.require_uppercase}
          onChange={(v) => save({ require_uppercase: v })}
        />
        <ToggleRow
          label="Require number"
          checked={policy.require_number}
          onChange={(v) => save({ require_number: v })}
        />
        <ToggleRow
          label="Require special character"
          checked={policy.require_special}
          onChange={(v) => save({ require_special: v })}
        />

        <div>
          <label className="block text-xs font-medium text-text-muted mb-1">
            Password expiry
          </label>
          <Select
            value={
              policy.password_expiry_days === null
                ? "never"
                : String(policy.password_expiry_days)
            }
            onChange={(e) => {
              const v = e.target.value;
              save({
                password_expiry_days: v === "never" ? null : Number(v),
              });
            }}
            options={[
              { value: "never", label: "Never" },
              { value: "30", label: "30 days" },
              { value: "60", label: "60 days" },
              { value: "90", label: "90 days" },
              { value: "180", label: "180 days" },
            ]}
          />
        </div>

        <div>
          <label className="block text-xs font-medium text-text-muted mb-1">
            Prevent password reuse
          </label>
          <Select
            value={String(policy.prevent_reuse_count)}
            onChange={(e) =>
              save({
                prevent_reuse_count: Number(e.target.value) as 0 | 3 | 5 | 10,
              })
            }
            options={[
              { value: "0", label: "Off" },
              { value: "3", label: "Last 3" },
              { value: "5", label: "Last 5" },
              { value: "10", label: "Last 10" },
            ]}
          />
        </div>

        <div>
          <label className="block text-xs font-medium text-text-muted mb-1">
            Lockout after failed attempts
          </label>
          <Select
            value={String(policy.max_failed_attempts)}
            onChange={(e) =>
              save({
                max_failed_attempts: Number(e.target.value) as 0 | 3 | 5 | 10,
              })
            }
            options={[
              { value: "0", label: "Off" },
              { value: "3", label: "3 attempts" },
              { value: "5", label: "5 attempts" },
              { value: "10", label: "10 attempts" },
            ]}
          />
        </div>

        <div>
          <label className="block text-xs font-medium text-text-muted mb-1">
            Lockout duration
          </label>
          <Select
            value={String(policy.lockout_duration_minutes)}
            onChange={(e) =>
              save({
                lockout_duration_minutes: Number(e.target.value) as
                  | 0
                  | 15
                  | 30
                  | 60,
              })
            }
            options={[
              { value: "15", label: "15 minutes" },
              { value: "30", label: "30 minutes" },
              { value: "60", label: "1 hour" },
              { value: "0", label: "Until admin unlocks" },
            ]}
          />
        </div>

        <div>
          <label className="block text-xs font-medium text-text-muted mb-1">
            Session timeout (idle)
          </label>
          <Select
            value={String(policy.session_timeout_minutes)}
            onChange={(e) =>
              save({
                session_timeout_minutes: Number(e.target.value) as
                  | 30
                  | 60
                  | 240
                  | 480
                  | 1440,
              })
            }
            options={[
              { value: "30", label: "30 minutes" },
              { value: "60", label: "1 hour" },
              { value: "240", label: "4 hours" },
              { value: "480", label: "8 hours" },
              { value: "1440", label: "24 hours" },
            ]}
          />
        </div>

        <div className="md:col-span-2">
          <label className="block text-xs font-medium text-text-muted mb-1">
            Require two-factor authentication
          </label>
          <Select
            value={policy.require_2fa}
            onChange={(e) =>
              save({
                require_2fa: e.target.value as
                  | "off"
                  | "super_admins"
                  | "admins_managers"
                  | "all_users",
              })
            }
            options={[
              { value: "off", label: "Off (opt-in per user)" },
              {
                value: "super_admins",
                label: "Required for Super Admins only",
              },
              {
                value: "admins_managers",
                label: "Required for Admins and Managers",
              },
              { value: "all_users", label: "Required for all users" },
            ]}
          />
          <p className="text-[11px] text-text-muted mt-1">
            Users who fall under this policy will be redirected to set
            up 2FA on their next sign-in until they activate it.
          </p>
        </div>
      </div>
    </Card>
  );
}

function ToggleRow({
  label,
  checked,
  onChange,
}: {
  label: string;
  checked: boolean;
  onChange: (v: boolean) => void;
}) {
  return (
    <label className="flex items-center justify-between rounded-md border border-border/40 px-3 py-2.5">
      <span className="text-sm text-text-primary">{label}</span>
      <input
        type="checkbox"
        className="h-4 w-4 rounded border-border accent-accent"
        checked={checked}
        onChange={(e) => onChange(e.target.checked)}
      />
    </label>
  );
}

// ── Sessions ───────────────────────────────────────────────────────

function SessionsSection() {
  const { addToast } = useToast();
  const [rows, setRows] = useState<SessionRow[]>([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState("");
  const [confirmOrgLogout, setConfirmOrgLogout] = useState(false);
  const [orgLogoutConfirmText, setOrgLogoutConfirmText] = useState("");
  const [orgLogoutBusy, setOrgLogoutBusy] = useState(false);

  const load = async () => {
    setLoading(true);
    try {
      setRows(await listOrgSessions());
    } catch {
      addToast("error", "Couldn't load sessions");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    load();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const visible = useMemo(
    () =>
      search.trim()
        ? rows.filter((r) =>
            (r.user_email || "").toLowerCase().includes(search.toLowerCase()),
          )
        : rows,
    [rows, search],
  );

  const forceLogout = async (r: SessionRow) => {
    if (!r.user_id) return;
    if (!confirm(`Sign ${r.user_email || "this user"} out of this device?`))
      return;
    try {
      await adminForceLogoutSession(r.user_id, r.id);
      addToast("success", "Session ended");
      load();
    } catch {
      addToast("error", "Couldn't end session");
    }
  };

  const unlockUser = async (r: SessionRow) => {
    if (!r.user_id) return;
    try {
      await adminUnlockUser(r.user_id);
      addToast("success", "User unlocked");
    } catch {
      addToast("error", "Couldn't unlock");
    }
  };

  const orgLogout = async () => {
    setOrgLogoutBusy(true);
    try {
      const res = await adminForceLogoutOrg();
      addToast("success", `${res.affected} users signed out`);
      setConfirmOrgLogout(false);
      setOrgLogoutConfirmText("");
      // Caller is now signed out too — let the app refresh.
      window.location.href = "/login";
    } catch (err: unknown) {
      const ax = err as { response?: { data?: { detail?: string } } };
      addToast("error", ax.response?.data?.detail || "Force logout failed");
    } finally {
      setOrgLogoutBusy(false);
    }
  };

  return (
    <>
      <Card>
        <CardHeader>
          <CardTitle>Active Sessions</CardTitle>
          <Input
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            placeholder="Filter by email"
            icon={<Search className="h-4 w-4" />}
            className="max-w-xs"
          />
        </CardHeader>

        {loading ? (
          <div className="py-10 text-center text-text-muted">
            <Loader2 className="h-6 w-6 animate-spin mx-auto" />
          </div>
        ) : visible.length === 0 ? (
          <div className="py-10 text-center text-sm text-text-muted">
            No active sessions.
          </div>
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead className="text-xs text-text-muted border-b border-border/50">
                <tr>
                  <th className="text-left py-2 px-2 font-medium">User</th>
                  <th className="text-left py-2 px-2 font-medium">Device</th>
                  <th className="text-left py-2 px-2 font-medium">IP</th>
                  <th className="text-left py-2 px-2 font-medium">
                    Last active
                  </th>
                  <th className="text-right py-2 px-2 font-medium">Actions</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-border/30">
                {visible.map((r) => (
                  <tr key={r.id}>
                    <td className="py-2 px-2 text-text-primary">
                      {r.user_email}
                    </td>
                    <td className="py-2 px-2 text-text-muted">
                      {r.device_info || "Unknown device"}
                    </td>
                    <td className="py-2 px-2 text-text-muted">
                      {r.ip_address || "—"}
                    </td>
                    <td className="py-2 px-2 text-text-muted">
                      {r.last_active_at ? formatDate(r.last_active_at) : "—"}
                    </td>
                    <td className="py-2 px-2 text-right">
                      <div className="inline-flex gap-1">
                        <button
                          onClick={() => unlockUser(r)}
                          className="p-1 rounded hover:bg-surface-hover text-text-muted"
                          title="Unlock user (clears lockout)"
                        >
                          <Unlock className="h-4 w-4" />
                        </button>
                        <button
                          onClick={() => forceLogout(r)}
                          className="p-1 rounded hover:bg-danger/10 text-danger"
                          title="Force logout this session"
                        >
                          <LogOut className="h-4 w-4" />
                        </button>
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </Card>

      <Card className="border-danger/30">
        <CardHeader>
          <CardTitle>
            <span className="inline-flex items-center gap-2 text-danger">
              <ShieldAlert className="h-4 w-4" />
              Emergency: force logout all users
            </span>
          </CardTitle>
        </CardHeader>
        <p className="text-sm text-text-muted">
          Immediately invalidates every active session in your
          organization &mdash; including yours. Use this if you suspect
          a security incident.
        </p>
        <Button
          variant="secondary"
          className="mt-3 text-danger border-danger/40"
          onClick={() => setConfirmOrgLogout(true)}
        >
          <LogOut className="h-4 w-4" />
          Force logout all users
        </Button>
      </Card>

      <Modal
        isOpen={confirmOrgLogout}
        onClose={() => (orgLogoutBusy ? null : setConfirmOrgLogout(false))}
        title="Force logout everyone?"
      >
        <p className="text-sm text-text-muted">
          This will immediately log out <strong>every user</strong> in your
          organization, including yourself. Type{" "}
          <code className="px-1 py-0.5 bg-surface-hover rounded text-text-primary">
            CONFIRM
          </code>{" "}
          to proceed.
        </p>
        <Input
          className="mt-3"
          value={orgLogoutConfirmText}
          onChange={(e) => setOrgLogoutConfirmText(e.target.value)}
          placeholder="Type CONFIRM"
        />
        <div className="mt-5 flex justify-end gap-2">
          <Button
            variant="secondary"
            onClick={() => setConfirmOrgLogout(false)}
            disabled={orgLogoutBusy}
          >
            Cancel
          </Button>
          <Button
            onClick={orgLogout}
            isLoading={orgLogoutBusy}
            disabled={orgLogoutConfirmText !== "CONFIRM"}
          >
            <ShieldAlert className="h-4 w-4" />
            Sign everyone out
          </Button>
        </div>
      </Modal>
    </>
  );
}

// ── Login events ───────────────────────────────────────────────────

function LoginEventsSection() {
  const { addToast } = useToast();
  const [rows, setRows] = useState<LoginEventRow[]>([]);
  const [loading, setLoading] = useState(true);
  const [eventType, setEventType] = useState<LoginEventType | "all">("all");
  const [days, setDays] = useState(30);

  const load = async () => {
    setLoading(true);
    try {
      const data = await listLoginEvents({
        event_type: eventType === "all" ? undefined : eventType,
        days,
      });
      setRows(data);
    } catch {
      addToast("error", "Couldn't load login activity");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    load();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [eventType, days]);

  return (
    <Card>
      <CardHeader>
        <CardTitle>Login Activity</CardTitle>
        <div className="flex items-center gap-2 flex-wrap">
          <Select
            value={eventType}
            onChange={(e) =>
              setEventType(e.target.value as LoginEventType | "all")
            }
            options={[
              { value: "all", label: "All events" },
              ...Object.entries(EVENT_LABELS).map(([v, l]) => ({
                value: v,
                label: l,
              })),
            ]}
          />
          <Select
            value={String(days)}
            onChange={(e) => setDays(Number(e.target.value))}
            options={[
              { value: "1", label: "Last 24 hours" },
              { value: "7", label: "Last 7 days" },
              { value: "30", label: "Last 30 days" },
              { value: "90", label: "Last 90 days" },
            ]}
          />
        </div>
      </CardHeader>

      {loading ? (
        <div className="py-10 text-center text-text-muted">
          <Loader2 className="h-6 w-6 animate-spin mx-auto" />
        </div>
      ) : rows.length === 0 ? (
        <div className="py-10 text-center text-sm text-text-muted">
          No events in this window.
        </div>
      ) : (
        <div className="overflow-x-auto">
          <table className="w-full text-sm">
            <thead className="text-xs text-text-muted border-b border-border/50">
              <tr>
                <th className="text-left py-2 px-2 font-medium">User</th>
                <th className="text-left py-2 px-2 font-medium">Event</th>
                <th className="text-left py-2 px-2 font-medium">IP</th>
                <th className="text-left py-2 px-2 font-medium">Device</th>
                <th className="text-left py-2 px-2 font-medium">When</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-border/30">
              {rows.map((r) => (
                <tr
                  key={r.id}
                  className={
                    r.event_type === "login_failed"
                      ? "bg-danger/5"
                      : undefined
                  }
                >
                  <td className="py-2 px-2 text-text-primary">
                    {r.user_email || (
                      <span className="text-text-muted italic">unknown</span>
                    )}
                  </td>
                  <td className="py-2 px-2">
                    <span
                      className={`inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium ${EVENT_TONE_CLASS[r.event_type]}`}
                    >
                      {EVENT_LABELS[r.event_type]}
                    </span>
                    {r.reason && (
                      <span className="ml-2 text-xs text-text-muted">
                        {r.reason}
                      </span>
                    )}
                  </td>
                  <td className="py-2 px-2 text-text-muted">
                    {r.ip_address || "—"}
                  </td>
                  <td className="py-2 px-2 text-text-muted">
                    {r.device_info || "—"}
                  </td>
                  <td className="py-2 px-2 text-text-muted whitespace-nowrap">
                    {formatDate(r.created_at)}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </Card>
  );
}

export default OrgSecurityPolicySettings;
